// commitMedia — the chat's "add a generated image URL to the current scene"
// commit path (F4 preview→accept→commit).
//
// This mirrors AddImageModal.jsx's `addBoxToScene` for the image case: it builds
// a `HotspotImages` box from a URL and commits it to the selected scene via the
// shared `createBox` helper. It deliberately does NOT go through JobWidgetHost's
// direct-callback job machinery — that path is for the async-job widget. AddImageModal
// already proves a self-contained `createBox(...)` call is the clean, robust way
// to add an image URL to a scene given the same editor props the chat holds, so
// we reuse that shape here (and leave JobWidgetHost's ADD_TO_SCENE flow untouched).
//
// `addImageToScene` is a factory: pass the editor `widgetProps` (the same props
// EditorApp threads into ChatPanel / JobWidgetHost) and get back an
// `async (imageUrl) => void` the tool-UI's Accept handler can call. It throws on
// the failure modes the caller must surface (no scene selected, missing
// dependencies, plugin not registered) so the card can show a "Couldn't add" state.

import getMediaBoxSize from "../../utils/getMediaBoxSize";

// Build an `addImageToScene(imageUrl)` bound to the given editor props. Returns a
// function so it can be memoized once per widgetProps in ChatPanel and handed to
// the tool-UI via context. Throws (rejects) on every condition the caller needs
// to know about; resolves once the box has been committed via onBoxAdded.
export function makeAddImageToScene(widgetProps) {
  const { onBoxAdded, boxes, navItems, navItemSelected, containedViews, containedViewSelected } =
    widgetProps || {};

  return async function addImageToScene(imageUrl) {
    if (!imageUrl || typeof imageUrl !== "string") {
      throw new Error("addImageToScene: no image URL");
    }
    if (typeof onBoxAdded !== "function") {
      throw new Error("addImageToScene: editor is not ready (no onBoxAdded)");
    }

    // Resolve the scene to add to: a selected contained view wins, else the
    // currently selected nav item (the current scene).
    const navItem =
      containedViewSelected && containedViews && containedViews[containedViewSelected]
        ? containedViews[containedViewSelected]
        : navItems && navItems[navItemSelected];

    if (!navItem) {
      // No scene selected — the caller surfaces this gracefully.
      throw new Error("addImageToScene: no scene selected");
    }

    // Lazy-require the editor singletons/helpers exactly as AddImageModal does, so
    // this module stays cheap to import and avoids load-order surprises.
    const IdealStudio = require("../../../core/editor/main").default;
    const { isSlide, isContainedView } = require("../../../common/utils");
    const { ID_PREFIX_BOX } = require("../../../common/constants");
    const { randomPositionGenerator } = require("../clipboard/clipboard.utils");
    const { createBox } = require("../../../common/common_tools");

    const pluginName = "HotspotImages";
    const plugin = IdealStudio.Plugins.get(pluginName);
    if (!plugin) {
      throw new Error("addImageToScene: HotspotImages plugin not registered");
    }

    const cv =
      containedViewSelected &&
      containedViews &&
      containedViews[containedViewSelected] &&
      isContainedView(containedViewSelected);
    const cvslide = cv && isSlide(navItem.type);

    const inASlide = (!cv && isSlide(navItem.type)) || cvslide;
    const parent = inASlide ? navItem.id : navItem.boxes && navItem.boxes[0];

    const { width, height, widthUnit, heightUnit } = await getMediaBoxSize(imageUrl, "image");

    const initialParams = {
      parent,
      container: 0,
      name: pluginName,
      position: {
        x: randomPositionGenerator(10, 30),
        y: randomPositionGenerator(10, 30),
        type: "absolute",
      },
      id: ID_PREFIX_BOX + Date.now(),
      page: cv ? containedViewSelected : navItem.id || navItemSelected || 0,
      url: imageUrl,
      width,
      height,
      widthUnit,
      heightUnit,
    };

    createBox(initialParams, pluginName, inASlide, onBoxAdded, boxes);
  };
}

export default makeAddImageToScene;
